class WiroModule {
  constructor(name, parentEl) {
    this.name = name || "wiroModule";
    this.parentEl = parentEl;
  }

  bindEvents() {}
  unbindEvents() {}
  render() {}

}

class WiroModuleHandler extends HTMLElement {
  /**
   * @param {WiroModule[]} modules
   */
  constructor(modules) {
    super();

    this.modules = {};
    for (const Module of modules) {
      const newModule = new Module(this);
      this.modules[newModule.name] = newModule;
    }
  }

  connectedCallback() {
    Object.values(this.modules).forEach((module) => module.bindEvents());
  }

  disconnectedCallback() {
    Object.values(this.modules).forEach((module) => module.unbindEvents());
  }
}

class WiroHTMLRenderer extends WiroModuleHandler {
  /**
   * @param {WiroModule[]} modules
   */
  constructor(modules = []) {
    super(modules);
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  prepareStringForRender(string) {
    if (!string) return;
    const domParser = new DOMParser();
    const dom = domParser.parseFromString(string, "text/html");
    return dom.body.firstChild;
  }

  render(dom) {
    if (!dom) return;
    if (typeof dom === "string") dom = this.prepareStringForRender(dom);
    this.innerHTML = "";
    this.appendChild(dom);
    Object.values(this.modules).forEach((module) => module.render());
  }
}

class RequestUtilityModule extends WiroModule {
  constructor(parentEl) {
    super("requestUtility", parentEl);
  }
  debounce(func, timeout = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }
}

class SearchspringAPIModule extends WiroModule {
  constructor(parentEl) {
    super("searchspring", parentEl);
  }

  get userId() {
    let id = this.getCookie("athosUserId");
    if (!id) id = this.setCookie("athosUserId", this.generateUUIDv4());
    return id;
  }
  get cartProducts() {
    return this.getCookie("ssCartProducts") || "";
  }
  get viewedProducts() {
    return this.getCookie("ssViewedProducts") || "";
  }
  get sessionId() {
    let id = this.getCookie("athosSessionId");
    if (!id) id = this.setCookie("athosSessionId", this.generateUUIDv4());
    return id;
  }
  get pageLoadId() {
    if (!window.searchspringPageLoadId) window.searchspringPageLoadId = this.generateUUIDv4();
    return window.searchspringPageLoadId;
  }
  get recentlyViewedFromCookie() {
    const value = document.cookie.split("; ").find((row) => row.startsWith("ssViewedProducts="));
    if (!value) return [];
    try {
      const raw = decodeURIComponent(value.split("=")[1]);
      if (raw.startsWith("[")) {
        return JSON.parse(raw);
      }
      return raw.split(",");
    } catch (e) {
      return [];
    }
  }
  get segmentedTagParam() {
    const params = new URLSearchParams(window.location.search);
    const tag = params.get("tag");
    if (!tag || !tag.startsWith("merch.segment/")) return;
    return tag;
  }
  get siteId() {
    return "atpsym";
  }
  get cookieExpiryDays() {
    return 3;
  }

  getCookie(name) {
    if (!name) return;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length < 2) return;
    return parts[1].split(";").shift();
  }

  setCookie(name, value) {
    if (!name || !value) return;
    const date = new Date();
    date.setTime(date.getTime() + this.cookieExpiryDays * 24 * 60 * 60 * 1000);
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  }

  decodeHtml(html) {
    if (!html) return;
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  generateUUIDv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  parseObjectIntoQueryParams(object) {
    let urlParams = "";
    Object.entries(object).forEach(([key, value], index) => {
      const prefix = index === 0 ? "?" : "&";
      urlParams = `${urlParams}${prefix}${key}=${value}`;
    });
    return urlParams;
  }

  toQueryParams(data, prefix = "") {
    const queryParams = [];
    for (const { key, value } of data) {
      const paramKey = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === "object") {
        queryParams.push(this.toQueryParams(value, paramKey));
      } else {
        queryParams.push(`${encodeURIComponent(paramKey)}=${encodeURIComponent(value)}`);
      }
    }
    return queryParams.join("&");
  }

  /**
   * Helper that sends API requests to Searchspring
   * https://docs.searchspring.com/reference/get-search
   *
   * @param additionalParams Key value store of additional parameters to add to the request
   *
   * @return The response data
   */
  async sendSearchRequest(additionalParams = {}, endpoint = 'search', beacon = true) {
    const requiredParams = [
      { key: "siteId", value: this.siteId },
      { key: "resultsFormat", value: "json" },
      { key: "userId", value: this.userId },
      { key: "sessionId", value: this.sessionId },
      { key: "pageLoadId", value: this.pageLoadId },
      { key: "domain", value: window.location.href },
      { key: "beacon", value: beacon }
    ];
    let urlParams = "";
    // Object.entries({ ...requiredParams, ...additionalParams }).forEach(([key, value], index) => {
    //   const prefix = index === 0 ? "?" : "&"; // The first param needs ?, all following ones need &
    //   urlParams = `${urlParams}${prefix}${key}=${value}`; // Concat on this param
    // });
    urlParams = this.toQueryParams([...requiredParams, ...additionalParams]);
    const url = `https://${this.siteId}.a.athoscommerce.net/v1/${endpoint}?${urlParams}`;

    const payload = {
      method: "GET",
      headers: { "Content-Type": "text/plain" },
    };

    const response = await fetch(url, payload);
    let data = await response.json();
    if (typeof data === "string") data = JSON.parse(data);
    if (Array.isArray(data)) data = data[0];
    return data;
  }
  async sendRecommendRequest(profileTag, additionalParams = {}, limit = 10) {
    const requiredParams = {
      siteId: this.siteId,
      resultsFormat: "json",
      userId: this.userId,
      sessionId: this.sessionId,
      pageLoadId: this.pageLoadId,
      domain: window.location.href,
      beacon: true,
      profiles: [
        {
          tag: profileTag,
          limit,
        },
      ],
    };
    const url = `https://${this.siteId}.a.p13n.athoscommerce.net/v1/recommend`;

    const payload = {
      method: "POST",
      body: JSON.stringify({ ...requiredParams, ...additionalParams }),
      headers: { "Content-Type": "text/plain" },
    };

    const response = await fetch(url, payload);
    let data = await response.json();
    if (typeof data === "string") data = JSON.parse(data);
    if (Array.isArray(data)) data = data[0];
    return data;
  }
  async sendAutocompleteRequest(searchTerm, additionalParams = {}, limit = 10) {
    const requiredParams = {
      siteId: this.siteId,
      resultsFormat: "json",
      userId: this.userId,
      sessionId: this.sessionId,
      pageLoadId: this.pageLoadId,
      domain: window.location.href,
      q: encodeURIComponent(searchTerm),
      suggestionCount: limit,
      language: "en",
      beacon: true,
    };
    const urlParams = this.parseObjectIntoQueryParams({ ...requiredParams, ...additionalParams });
    const url = `https://${this.siteId}.a.athoscommerce.net/v1/suggest${urlParams}`;

    const payload = {
      method: "GET",
      headers: { "Content-Type": "text/plain" },
    };

    const response = await fetch(url, payload);
    let data = await response.json();
    if (typeof data === "string") data = JSON.parse(data);
    if (Array.isArray(data)) data = data[0];
    return data;
  }
}

// Beacon tracking
class SearchspringBeaconModule extends WiroModule {
  constructor(parentEl) {
    super("beacon", parentEl);
    this.siteId = "atpsym";
    this.beaconUrl = `https://analytics.athoscommerce.net/beacon/v2/${this.siteId}`;
    this.profileEventId = null;
    this.renderedResults = [];
    this.impressionSent = new Set();
  }

  generateUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  _getBaseContext() {
    const ss = this.parentEl.modules.searchspring;
    return {
      userId: ss.userId,
      sessionId: ss.sessionId,
      pageLoadId: ss.pageLoadId,
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
      initiator: "kickgame/custom/1.0",
    };
  }

  _getEventContext(profileTag, placement) {
    return {
      type: "product-recommendation",
      tag: profileTag,
      placement: placement,
    };
  }

  async sendRenderBatch(profileTag, placement = "other", seedProducts = [], results = [], responseId = null) {
    if (!profileTag) return;

    const context = this._getBaseContext();

    this.profileEventId = responseId || this.generateUUID();
    this.renderedResults = results;
    this.impressionSent = new Set();

    const payload = {
      context: context,
      data: {
        tag: profileTag,
        responseId: this.profileEventId,
      },
    };

    try {
      await fetch(`${this.beaconUrl}/recommendations/render`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn("Beacon render error:", error);
    }
  }

  async sendImpressionBatch(profileTag, placement, seedProducts = [], visibleResults = []) {
    if (!this.profileEventId || !visibleResults.length) return;

    const context = this._getBaseContext();

    const newResults = visibleResults.filter((r) => {
      const pid = r.id || r.mappings?.core?.uid;
      return !this.impressionSent.has(pid);
    });

    if (!newResults.length) return;

    const data = {
      context: {
        ...context,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
        initiator: "kickgame-theme",
      },
      data: {
        responseId: this.profileEventId,
        tag: profileTag,
        results: newResults.map((result) => {
          const productId = result.id || result.mappings?.core?.uid;
          return {
            type: "product",
            parentId: productId,
            uid: productId,
          };
        }),
        banners: [],
      },
    };

    try {
      await fetch(`${this.beaconUrl}/recommendations/impression`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.warn("Beacon impression batch error:", error);
    }
  }

  async sendCarouselClickBatch(profileTag, placement, seedProducts = [], newVisibleResults = []) {
    if (!this.profileEventId) return;

    const context = this._getBaseContext();

    const newResults = newVisibleResults.filter((r) => {
      const pid = r.id || r.mappings?.core?.uid;
      return !this.impressionSent.has(pid);
    });

    const data = {
      context: {
        ...context,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
        initiator: "kickgame-theme",
      },
      data: {
        responseId: this.profileEventId,
        tag: profileTag,
        results: newResults.map((result) => {
          const productId = result.id || result.mappings?.core?.uid;
          return {
            type: "product",
            parentId: productId,
            uid: productId,
          };
        }),
        banners: [],
      },
    };

    try {
      await fetch(`${this.beaconUrl}/recommendations/impression`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.warn("Beacon carousel click batch error:", error);
    }
  }

  async sendProductClickBatch(profileTag, placement, seedProducts = [], clickedResult, responseId) {
    if(!this.profileEventId && responseId) this.profileEventId = responseId;
    if (!this.profileEventId || !clickedResult) return;

    const context = this._getBaseContext();
    const eventContext = this._getEventContext(profileTag, placement);
    const seed = seedProducts && seedProducts.length > 0 ? seedProducts : [""];
    const productId = clickedResult.id || clickedResult.mappings?.core?.uid;

    const data = {
      context: {
        ...context,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
        initiator: "kickgame-theme",
      },
      data: {
        responseId: this.profileEventId,
        tag: profileTag,
        results: [
          {
            type: "product",
            parentId: productId,
            uid: productId,
          },
        ],
      },
    };

    try {
      fetch(`${this.beaconUrl}/recommendations/clickthrough`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(data),
        keepalive: true,
      });
    } catch (error) {
      console.warn("Beacon product click error:", error);
    }
  }

  getSeedProducts() {
    const seeds = [];
    const productJson = document.querySelector('script[type="application/json"][data-product-json]');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        if (product.id) {
          seeds.push({ sku: String(product.id) });
        }
      } catch (e) {
        console.warn("Failed to parse product JSON:", e);
      }
    }
    if (window.location.pathname.includes("/cart")) {
      const cartProducts = this.parentEl.modules.searchspring.cartProducts;
      if (cartProducts) {
        cartProducts.split(",").forEach((sku) => {
          if (sku) seeds.push({ sku: sku.trim() });
        });
      }
    }
    return seeds;
  }

  // Autocomplete render
  async sendAutocompleteRender(responseId) {
    if (!responseId) return;
    const payload = {
      context: this._getBaseContext(),
      data: {
        responseId: responseId,
      },
    };
    try {
      await fetch(`${this.beaconUrl}/autocomplete/render`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn("Beacon autocomplete render error:", error);
    }
  }

  // Autocomplete impression
  async sendAutocompleteImpression(responseId, results = []) {
    if (!responseId) return;
    const payload = {
      context: this._getBaseContext(),
      data: {
        responseId: responseId,
        results: results.map((product) => {
          const uid = product.uid || product.id || product.mappings?.core?.uid;
          return {
            type: "product",
            parentId: product.parentId ? product.parentId : uid,
            uid: uid,
            sku: product.sku ? product.sku : null
          };
        }),
        banners: [],
      },
    };
    try {
      await fetch(`${this.beaconUrl}/autocomplete/impression`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn("Beacon autocomplete impression error:", error);
    }
  }

  // Autocomplete clickthrough
  async sendAutocompleteClickthrough(responseId, product) {
    if (!responseId || !product) return;
    const uid = product.uid || product.id || product.mappings?.core?.uid;
    const payload = {
      context: this._getBaseContext(),
      data: {
        responseId: responseId,
        results: [
          {
            type: "product",
            parentId: product.parentId ? product.parentId : uid,
            uid: uid,
            sku: product.sku ? product.sku : null
          },
        ],
      },
    };
    try {
      fetch(`${this.beaconUrl}/autocomplete/clickthrough`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch (error) {
      console.warn("Beacon autocomplete clickthrough error:", error);
    }
  }

  // Autocomplete redirect
  async sendAutocompleteRedirect(responseId, redirectUrl) {
    if (!responseId || !redirectUrl) return;
    const payload = {
      context: this._getBaseContext(),
      data: {
        responseId: responseId,
        redirect: redirectUrl,
      },
    };
    try {
      fetch(`${this.beaconUrl}/autocomplete/redirect`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch (error) {
      console.warn("Beacon autocomplete redirect error:", error);
    }
  }

  async sendRecommendationsAddToCart(responseId, tag, product, qty = 1, price = 0) {
    if (!responseId || !product) return;
    const uid = String(product.uid || product.id || product.mappings?.core?.uid);
    const payload = {
      context: this._getBaseContext(),
      data: {
        tag: tag,
        responseId: responseId,
        results: [
          {
            parentId: product.parentId ? product.parentId : uid,
            uid: uid,
            sku: product.sku ? product.sku : null,
            qty: qty,
            price: price,
          },
        ],
      },
    };
    try {
      await fetch(`${this.beaconUrl}/recommendations/addtocart`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn("Beacon recommendations addtocart error:", error);
    }
  }

  async sendSearchAddToCart(responseId, product, qty = 1, price = 0) {
    if (!responseId || !product) return;
    const uid = String(product.uid || product.id || product.mappings?.core?.uid);
    const payload = {
      context: this._getBaseContext(),
      data: {
        responseId: responseId,
        results: [
          {
            parentId: product.parentId ? product.parentId : uid,
            uid: uid,
            sku: product.sku ? product.sku : null,
            qty: qty,
            price: price,
          },
        ],
      },
    };
    try {
      await fetch(`${this.beaconUrl}/search/addtocart`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn("Beacon search addtocart error:", error);
    }
  }

  async sendCategoryAddToCart(responseId, product, qty = 1, price = 0) {
    if (!responseId || !product) return;
    const uid = String(product.uid || product.id || product.mappings?.core?.uid);
    const payload = {
      context: this._getBaseContext(),
      data: {
        responseId: responseId,
        results: [
          {
            parentId: product.parentId ? product.parentId : uid,
            uid: uid,
            sku: product.sku ? product.sku : null,
            qty: qty,
            price: price,
          },
        ],
      },
    };
    try {
      await fetch(`${this.beaconUrl}/category/addtocart`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn("Beacon category addtocart error:", error);
    }
  }
}

class SearchspringCarouselHandler extends WiroHTMLRenderer {
  constructor(modules = [], profileTag = "home", productIds, productCardComponent) {
    super([SearchspringAPIModule, SearchspringBeaconModule, ...modules]);

    this.profileTag = this.dataset.profileTag || profileTag || "";
    this.productIds = productIds || this.dataset.productIds;
    this.productCardComponent = productCardComponent || this.dataset.productCardComponent || "wiro-product-card";
    this.carouselOptions = this.parseCarouselOptions();
    this.limit = this.dataset.limit || 20;
    this.placement = this.dataset.placement || this._getDefaultPlacement();
    this.showCategorySelector = this.dataset.categorySelector === "true";
    this.results = [];

    if (this.dataset.seedProducts) {
      try {
        this.seedProducts = JSON.parse(this.dataset.seedProducts);
      } catch (e) {
        this.seedProducts = null;
      }
    }
  }

  _getDefaultPlacement() {
    const placementMap = {
      home: "home-page",
      "cross-sell": "product-page",
      similar: "product-page",
      "view-cart": "basket-page",
      "recently-viewed": "other",
    };
    return placementMap[this.profileTag] || "other";
  }

  async connectedCallback() {
    const additionalParams = {
      ...(this.productIds && { products: this.productIds.split(",") }),
    };

    const data = await this.modules.searchspring.sendRecommendRequest(this.profileTag, additionalParams, this.limit);

    this.results = data?.results || [];
    const domString = this.getDomString(data.results);
    const dom = this.prepareStringForRender(domString);
    const productCards = this.getProductCards(data.results);
    const insertionPoint = dom.querySelector("[data-card-container]");
    productCards.forEach((card) => insertionPoint.appendChild(card));

    this.render(dom);

    // Beacon: render batch
    if (this.modules.beacon && this.profileTag && data.results.length) {
      const seeds = this.seedProducts || this.modules.beacon.getSeedProducts();
      this.modules.beacon.sendRenderBatch(this.profileTag, this.placement, seeds, data.results, data.responseId);
    }

    if(!this.results.length) return
    this.initCarousel(this.results.length);
    this.bindCategoryClicks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.carousel) this.carousel.destroy(true);
  }

  initCarousel(slideCount) {
    this.carousel = new Splide(this.querySelector(".splide"), this.getCarouselOptions());
    if (window.splide && window.splide.Extensions && window.splide.Extensions.Grid && slideCount > 12) {
      this.carousel.mount({ Grid: window.splide.Extensions.Grid });
    } else {
      this.carousel.mount();
    }

    this._setupImpressionObserver();

    this._setupCarouselClickTracking();

    this._setupProductClickTracking();
  }

  _setupImpressionObserver() {
    if (!this.modules.beacon) return;
    const self = this;

    this._impressionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const visibleResults = self._getVisibleResults();
            const seeds = self.seedProducts || self.modules.beacon.getSeedProducts();
            self.modules.beacon.sendImpressionBatch(self.profileTag, self.placement, seeds, visibleResults);
            self._impressionObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 },
    );

    const section = this.querySelector(".splide");
    if (section) this._impressionObserver.observe(section);
  }

  _getVisibleResults() {
    if (!this.carousel || !this.results) return this.results || [];

    const track = this.querySelector(".splide__track");
    if (!track) return this.results;

    const containerRect = track.getBoundingClientRect();
    const slides = this.querySelectorAll(".splide__slide");
    const visibleIndices = [];

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      if (rect.left >= containerRect.left - 10 && rect.right <= containerRect.right + 10) {
        visibleIndices.push(index);
      }
    });

    if (visibleIndices.length === 0) return this.results;
    return visibleIndices.map((i) => this.results[i]).filter(Boolean);
  }

  _setupCarouselClickTracking() {
    if (!this.modules.beacon || !this.carousel) return;
    const self = this;

    this.carousel.on("moved", () => {
      const visibleResults = self._getVisibleResults();
      const seeds = self.seedProducts || self.modules.beacon.getSeedProducts();
      self.modules.beacon.sendCarouselClickBatch(self.profileTag, self.placement, seeds, visibleResults);
    });
  }

  _setupProductClickTracking() {
    if (!this.modules.beacon || !this.results) return;
    const self = this;

    this.addEventListener("click", (e) => {
      const link = e.target.closest("a[href]");
      if (!link) return;

      const slide = link.closest(".splide__slide");
      if (!slide) return;

      const slides = [...self.querySelectorAll(".splide__slide")];
      const index = slides.indexOf(slide);
      if (index === -1 || !self.results[index]) return;

      const seeds = self.seedProducts || self.modules.beacon.getSeedProducts();
      self.modules.beacon.sendProductClickBatch(self.profileTag, self.placement, seeds, self.results[index]);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.carousel) this.carousel.destroy(true);
    if (this._impressionObserver) this._impressionObserver.disconnect();
  }

  bindCategoryClicks() {
    const categories = this.querySelectorAll("shadow-scroll-container [category-selector]");
    categories.forEach((el) => {
      el.addEventListener("click", (event) => {
        categories.forEach((catEl) => catEl.classList.remove("category-selector--active"));
        const category = (el.dataset.category || "").trim();
        this.updateProductCardsByCategory(category);
        event.target.classList.add("category-selector--active");
      });
    });
  }

  updateProductCardsByCategory(category) {
    if (this.carousel) {
      this.carousel.destroy(true);
      this.carousel = null;
    }
    if (!this.results || !this.results.length) return;
    const insertionPoint = this.querySelector("[data-card-container]");
    if (!insertionPoint) return;

    const filtered = category
      ? this.results.filter(
        (item) => (item?.attributes?.mfield_tabledetails_category2 || "").trim() === category,
      )
      : this.results;

    insertionPoint.innerHTML = "";
    this.getProductCards(filtered).forEach((card) => insertionPoint.appendChild(card));

    this.initCarousel(filtered.length);
  }

  parseCarouselOptions() {
    const defaultOptions = {
      type: "loop",
      perPage: 8,
      arrows: false,
      pagination: false,
      breakpoints: {
        640: { perPage: 2 },
      },
    };
    if (!this.dataset.carouselOptions) return defaultOptions;

    try {
      const customOptions = JSON.parse(this.dataset.carouselOptions);
      return { ...defaultOptions, ...customOptions };
    } catch (error) {
      console.warn("Invalid JSON in data-carousel-options, using defaults:", error);
      return defaultOptions;
    }
  }

  getCarouselOptions() {
    return this.carouselOptions;
  }

  getProductCards(results) {
    return results.map((product) => {
      const card = document.createElement(this.productCardComponent);
      card.setProduct(product);
      const cardSplideWrapper = this.prepareStringForRender(
        `<li class="splide__slide tw-w-[172px] max-sm:!tw-w-[46%]"></li>`,
      );
      cardSplideWrapper.appendChild(card);
      return cardSplideWrapper;
    });
  }

  getDomString(results) {
    if (!results.length) return `<p>No results found</p>`;
    const listCategories = [...new Set(results
      .map(a => a.attributes.mfield_tabledetails_category2)
      .filter(Boolean))];

    const categoryMarkup = listCategories.length ?
      `<div class="tw-relative">
        <shadow-scroll-container class="tw-h-[45px] tw-flex tw-gap-2 tw-overflow-x-scroll [scrollbar-width:none] tw-mb-3">
          <span category-selector data-category="" class="tw-bg-[#F2EFE7] tw-text-[#636363] tw-rounded-[4px] tw-p-3 tw-font-semibold tw-uppercase tw-whitespace-nowrap tw-text-nowrap tw-h-[42px] md:tw-h-[44px] tw-cursor-pointer category-selector--active">
              All
          </span>
          ${listCategories.map(cat =>
        `<span category-selector data-category="${cat}" class="tw-bg-[#F2EFE7] tw-text-[#636363] tw-rounded-[4px] tw-p-3 tw-font-semibold tw-uppercase tw-whitespace-nowrap tw-text-nowrap tw-h-[42px] md:tw-h-[44px] tw-cursor-pointer">
              ${cat}
            </span>
            `
      ).join("")}
        </shadow-scroll-container>
      </div>`
      : "";

    return `
      <div>
        ${this.showCategorySelector ? categoryMarkup : ""}
        <section class="splide gradient-edges" aria-labelledby="carousel-heading">
          <div class="splide__track">
            <ul class="splide__list" data-card-container>
            </ul>
          </div>
        </section>
      </div>
    `;
  }
}
if (!customElements.get("searchspring-carousel-handler"))
  customElements.define("searchspring-carousel-handler", SearchspringCarouselHandler);

class WiroProductCard extends WiroHTMLRenderer {
  constructor() {
    super([]);
  }
  setProduct(product) {
    this.product = product;
  }
  parseBoostAPIProductData() {
    const p = this.product || {};
    if (p.mappings && p.mappings.core && p.attributes) {
      const core = p.mappings.core || {};
      const attributes = p.attributes || {};
      if (!this.product.id && (core.uid || core.id)) this.product.id = core.uid || core.id;
      return {
        name: core.name || "",
        url: core.url || "#",
        imageUrl: core.imageUrl || core.thumbnailImageUrl || "",
        price: Number(core.price) || 0,
        msrp: Number(core.msrp) || 0,
        brand: core.brand || "",
        okendo_summarydata: attributes.mfield_okendo_summarydata || null,
        size_available: attributes.ss_size_in_stock || null,
        brand_collab: attributes.mfield_custom_brand_collaboration || null,
        images: attributes.images || [],
      };
    } else {
      const uid = p.uid;
      const url = p.url || (p.handle ? `/products/${p.handle}` : "#");
      const okendo_summarydata = p ? p.mfield_okendo_summarydata : null;
      const size_available = p ? p.ss_size_in_stock : null;
      const imageUrl = p.thumbnailImageUrl;
      const images = p.images || [];
      let price = 0;
      if (p.price) price = Number(p.price);

      let msrp = 0;
      if (p.msrp) msrp = Number(p.msrp);
      this.product.id = uid;

      const brand_collab = p ? p.mfield_custom_brand_collaboration : null;
      return {
        name: p.name || p.title || "",
        url,
        imageUrl,
        price: Number(price) || 0,
        msrp: Number(msrp) || 0,
        brand: p.vendor || p.brand || "",
        okendo_summarydata,
        size_available,
        brand_collab,
        images,
      };
    }
  }
  connectedCallback() {
    if (!this.product) return;
    const { name, url, imageUrl, price } = this.parseBoostAPIProductData();

    const card = this.prepareStringForRender(`
      <a href="${url}">
        <div class="card tw-flex tw-items-center tw-text-center">
          <img class="tw-size-[130px] sm:tw-size-30" src="${imageUrl}" alt="${name}">
          <p class="tw-text-center tw-text-small tw-mt-2 tw-mb-1 sm:tw-line-clamp-2">${name}</p>
          <p class="tw-font-semibold tw-mt-auto tw-text-small">${this.formatPrice(price)}</p>
        </div>
      </a>
    `);

    this.render(card);
  }

  formatPrice(price) {
    return `£${price}`;
  }
}
if (!customElements.get("wiro-product-card")) customElements.define("wiro-product-card", WiroProductCard);
